import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Play, Pause, FastForward, Swords, Share2, Gauge, Cuboid, AlignLeft, LoaderCircle, CheckCircle2, Coins } from "lucide-react";
import Modal from "@/components/Modal";
import ErrorBoundary from "@/components/ErrorBoundary";
import ShareModal from "@/components/ShareModal";
import { BattleLogFeed } from "@/components/BattleLogFeed";
import { FightTypeSummaryCard } from "@/components/FightTypeSummaryCard";
import api, { apiErr } from "@/lib/api";
import { scheduleBattle3DMount, warmBattle3DResources } from "@/lib/battle3dWarmup";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CLASS_IMAGE } from "@/lib/game";
import { extractRoundNumber, linePlaybackFactor } from "@/lib/battlePresentation";
import { buildBattleReplay3DData, getBattleReplay3DFrame } from "@/lib/battle3dReplay";
const BattleReplay3DViewer = lazy(() => import("@/components/battle3d/BattleReplay3DViewer"));

const BASE_STEP_MS = 3400;  // Base delay (1x speed)

// Attack flash effect component (Improvement #2)
function AttackFlash({ from, to, show }) {
  if (!show) return null;
  
  const isLeftToRight = from === "left";
  
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className={`animate-attack-flash ${isLeftToRight ? 'rotate-0' : 'rotate-180'}`}>
        <svg width="120" height="4" viewBox="0 0 120 4" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="2" x2="120" y2="2" stroke="url(#flash-gradient)" strokeWidth="4" />
          <defs>
            <linearGradient id="flash-gradient" x1="0" y1="0" x2="120" y2="0">
              <stop offset="0%" stopColor="rgba(239, 68, 68, 0)" />
              <stop offset="50%" stopColor="rgba(239, 68, 68, 1)" />
              <stop offset="100%" stopColor="rgba(239, 68, 68, 0)" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

// Builds a viewer-aware result line
function perspectiveSummary(battle, myId) {
  if (!battle || !battle.g1_owner || !battle.g2_owner) return battle?.summary || "";
  const winnerIsG1 = battle.winner_id === battle.g1_id;
  const w = winnerIsG1
    ? { name: battle.g1, owner: battle.g1_owner, ownerName: battle.g1_owner_name }
    : { name: battle.g2, owner: battle.g2_owner, ownerName: battle.g2_owner_name };
  const l = winnerIsG1
    ? { name: battle.g2, owner: battle.g2_owner, ownerName: battle.g2_owner_name }
    : { name: battle.g1, owner: battle.g1_owner, ownerName: battle.g1_owner_name };
  if (!w.name || !l.name) return battle.summary || "";
  const label = (g) => (g.owner === myId ? `your gladiator ${g.name}` : `${g.ownerName || "A lanista"}'s ${g.name}`);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  return `${cap(label(w))} defeated ${label(l)} in ${battle.rounds} round${battle.rounds === 1 ? "" : "s"}.`;
}

// Gladiator portrait with health visualization
function GladiatorPortrait({ name, gladClass, gladId, hp, maxHp, position, isCrit = false, isWinner = false, battleEnded = false }) {
  const navigate = useNavigate();
  const hpPct = maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
  const damagePct = 100 - hpPct;
  const imageUrl = CLASS_IMAGE[gladClass] || CLASS_IMAGE.Provocator;
  
  // Darken portrait based on damage (more damage = darker)
  const darkenAmount = Math.floor(damagePct * 0.6); // 0-60% darkening
  
  // Victory/defeat visual states (Improvement #4)
  const getPortraitBorderClass = () => {
    if (!battleEnded) return "border-zinc-700";
    if (isWinner) return "border-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.5)]"; // Gold glow for winner
    return "border-zinc-800 opacity-60"; // Dimmed for loser
  };
  
  const getPortraitFilterStyle = () => {
    let brightness = 100 - darkenAmount;
    if (battleEnded && !isWinner) brightness *= 0.7; // Further dim loser
    if (battleEnded && isWinner) brightness = Math.min(120, brightness * 1.1); // Brighten winner
    return { filter: `brightness(${brightness}%) ${battleEnded && !isWinner ? 'grayscale(40%)' : ''}` };
  };
  
  return (
    <div className={`flex flex-col gap-2 ${position === "left" ? "items-start" : "items-end"}`}>
      {/* Gladiator name - clickable */}
      <button
        onClick={() => navigate(`/gladiator/${gladId}`)}
        className={`text-xs font-cinzel uppercase tracking-wide transition-colors ${
          battleEnded && isWinner 
            ? "text-amber-300 hover:text-amber-200 font-bold animate-pulse" 
            : battleEnded && !isWinner
            ? "text-zinc-600 hover:text-zinc-500"
            : "text-amber-300 hover:text-amber-200"
        } ${position === "left" ? "text-left" : "text-right"}`}
      >
        {name}
      </button>
      
      {/* Portrait container with animations */}
      <div 
        className={`relative w-24 h-24 border-2 transition-all duration-300 ${getPortraitBorderClass()} ${
          isCrit ? "scale-105 animate-shake" : ""
        }`}
      >
        {/* Base portrait image */}
        <img 
          src={imageUrl} 
          alt={gladClass} 
          className="w-full h-full object-cover object-top transition-all duration-500"
          style={getPortraitFilterStyle()}
        />
        
        {/* Red damage overlay (grows as health decreases) */}
        <div 
          className="absolute inset-0 bg-red-900/70 transition-all duration-500"
          style={{ 
            clipPath: `inset(${100 - damagePct}% 0 0 0)`,
            opacity: damagePct > 0 ? 0.8 : 0
          }}
        />
        
        {/* Critical hit blood effect */}
        {isCrit && (
          <div className="absolute inset-0 animate-blood-splash">
            <div className="absolute inset-0 bg-red-600/40 animate-pulse" />
          </div>
        )}
        
        {/* Victory glow effect */}
        {battleEnded && isWinner && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-transparent to-amber-600/20 animate-pulse" />
          </div>
        )}
      </div>
      
      {/* Health bar - improved with smooth animation and glow (Improvement #1) */}
      <div className="w-24 h-2 bg-zinc-900 border border-zinc-700 relative overflow-hidden">
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-600 to-red-500 transition-all duration-700 ease-out"
          style={{ 
            width: `${hpPct}%`,
            boxShadow: hpPct > 0 ? '0 0 8px rgba(220, 38, 38, 0.6)' : 'none'
          }}
        />
      </div>
    </div>
  );
}

function BattleReplay3DPreloadShell() {
  return (
    <div
      data-testid="battle-3d-preload-shell"
      className="border border-amber-900/40 bg-gradient-to-br from-zinc-950 via-[#130d0a] to-amber-950/20 rounded-sm p-4 space-y-3 shadow-[0_24px_70px_rgba(0,0,0,0.45)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300">
            <Cuboid size={14} /> 3D Battle Viewer
          </div>
          <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
            Preparing the arena view before the heavy 3D scene mounts.
          </p>
        </div>
      </div>
      <div className="relative overflow-hidden rounded-sm border border-zinc-800 bg-zinc-950 shadow-[0_0_35px_rgba(245,158,11,0.12)]">
        <div className="aspect-[16/9] md:aspect-[16/7.1] w-full relative flex flex-col items-center justify-center gap-3 bg-zinc-950">
          <LoaderCircle size={24} className="text-amber-400 animate-spin" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Loading 3D Battle…</span>
        </div>
      </div>
    </div>
  );
}

function BattleReplay3DLoadCurtain({ phase }) {
  const steps = [
    { key: "preparing", label: "Opening the 3D viewer" },
    { key: "warming", label: "Warming arena assets" },
    { key: "rendering", label: "Rendering the first frame" },
  ];
  const currentIndex = phase === "rendering" ? 2 : phase === "warming" ? 1 : 0;

  return (
    <div data-testid="battle-replay-3d-loading-curtain" className="fixed inset-0 z-[70] pointer-events-none flex items-start justify-center px-4 pt-24 sm:pt-28">
      <div className="absolute inset-0 bg-black/72 backdrop-blur-[2px]" />
      <div className="relative w-full max-w-xl border border-amber-700/40 bg-zinc-950/95 shadow-[0_30px_120px_rgba(0,0,0,0.55)] rounded-sm overflow-hidden">
        <div className="border-b border-amber-900/30 bg-gradient-to-r from-amber-950/50 via-zinc-950 to-zinc-950 px-5 py-4 flex items-start gap-3">
          <div className="mt-0.5 rounded-full border border-amber-600/40 bg-amber-950/40 p-2 text-amber-300">
            <LoaderCircle size={18} className="animate-spin" />
          </div>
          <div>
            <div className="font-cinzel text-base uppercase tracking-[0.18em] text-amber-300">Preparing 3D Battle</div>
            <p className="mt-1 text-sm text-zinc-300 leading-relaxed">
              The arena is loading now. Stay here — the replay will appear as soon as the first frame is ready.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Load progress</div>
          <div className="space-y-2">
            {steps.map((step, index) => {
              const complete = index < currentIndex;
              const active = index === currentIndex;
              return (
                <div key={step.key} className={`flex items-center justify-between gap-3 rounded-sm border px-3 py-2 ${complete || active ? "border-amber-700/40 bg-amber-950/20" : "border-zinc-800 bg-zinc-950/70"}`}>
                  <span className={`text-xs uppercase tracking-[0.16em] ${complete || active ? "text-amber-200" : "text-zinc-500"}`}>{step.label}</span>
                  <span className={`text-[10px] uppercase tracking-[0.18em] ${complete ? "text-emerald-300" : active ? "text-amber-300" : "text-zinc-600"}`}>
                    {complete ? "Done" : active ? "Working" : "Queued"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReplayInlineNotices({ notices }) {
  if (!notices?.length) return null;

  const toneClasses = {
    success: "border-emerald-800/40 bg-emerald-950/20 text-emerald-200",
    reward: "border-amber-800/40 bg-amber-950/20 text-amber-200",
    info: "border-sky-800/40 bg-sky-950/20 text-sky-200",
  };

  const iconFor = (tone) => {
    if (tone === "reward") return <Coins size={14} className="text-amber-300 shrink-0 mt-0.5" />;
    return <CheckCircle2 size={14} className="text-emerald-300 shrink-0 mt-0.5" />;
  };

  return (
    <div data-testid="battle-replay-inline-notices" className="mb-4 space-y-2">
      {notices.map((notice, index) => (
        <div
          key={notice.id || `${notice.message}-${index}`}
          data-testid={`battle-replay-inline-notice-${notice.tone || "info"}`}
          className={`flex items-start gap-2 border px-3 py-2 rounded-sm text-sm ${toneClasses[notice.tone] || toneClasses.info}`}
        >
          {iconFor(notice.tone)}
          <span>{notice.message}</span>
        </div>
      ))}
    </div>
  );
}

function BattleReplayClassicView({
  battle,
  currentHP,
  hitGlad,
  done,
  attackDir,
  logRef,
  playing,
  shown,
  winnerId,
}) {
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start mb-4 bg-zinc-950/80 p-4 sm:p-6 rounded border border-zinc-800 relative">
        <AttackFlash from={attackDir} to={attackDir === "left" ? "right" : "left"} show={attackDir !== null && !done} />
        {currentHP && (
          <GladiatorPortrait
            key={`g1-${shown}-${currentHP.g1_hp}`}
            name={battle.g1_name || battle.g1}
            gladClass={battle.g1_class || "Provocator"}
            gladId={battle.g1_id}
            hp={currentHP.g1_hp}
            maxHp={currentHP.g1_max_hp}
            position="left"
            isCrit={hitGlad === "g1"}
            isWinner={winnerId === battle.g1_id}
            battleEnded={done}
          />
        )}

        <BattleLogFeed
          lines={battle.log}
          shown={shown}
          scrollRef={logRef}
          loading={playing && shown < battle.log.length}
          dataTestId="replay-log"
        />

        {currentHP && (
          <GladiatorPortrait
            key={`g2-${shown}-${currentHP.g2_hp}`}
            name={battle.g2_name || battle.g2}
            gladClass={battle.g2_class || "Provocator"}
            gladId={battle.g2_id}
            hp={currentHP.g2_hp}
            maxHp={currentHP.g2_max_hp}
            position="right"
            isCrit={hitGlad === "g2"}
            isWinner={winnerId === battle.g2_id}
            battleEnded={done}
          />
        )}
      </div>
    </>
  );
}

export default function BattleReplay({ battleId, battle: battleProp, open, onClose, title = "Play-by-Play", exhibition = false, rewards = null, shareable = false, startIn3D = false, inlineNotices = null }) {
  const { user, setUser } = useAuth();
  const [battle, setBattle] = useState(null);
  const [shown, setShown] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 2x, 3x, 4x
  const prefersDefault3D = user?.default_battle_view === "3d";
  const [show3D, setShow3D] = useState(startIn3D || prefersDefault3D);
  const [viewerMountReady, setViewerMountReady] = useState(false);
  const [threeDLoadPhase, setThreeDLoadPhase] = useState(show3D ? "preparing" : "idle");
  const [savingViewPref, setSavingViewPref] = useState(false);
  const [activeInlineNotices, setActiveInlineNotices] = useState([]);
  const logRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    const wants3D = startIn3D || prefersDefault3D;
    setShown(0);
    setPlaying(false);
    setShow3D(wants3D);
    setViewerMountReady(false);
    setThreeDLoadPhase(wants3D ? "preparing" : "idle");
    warmBattle3DResources();
    if (battleProp) { setBattle(battleProp); setShown(1); setPlaying(true); return; }
    if (!battleId) return;
    setBattle(null);
    api.get(`/battles/${battleId}`)
      .then(({ data }) => { setBattle(data); setShown(1); setPlaying(true); })
      .catch((e) => { toast.error(apiErr(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, battleId, battleProp]);

  useEffect(() => {
    if (!open) {
      setActiveInlineNotices([]);
      return;
    }
    setActiveInlineNotices(Array.isArray(inlineNotices) ? [...inlineNotices] : []);
  }, [open, inlineNotices]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    window.__lanistaBattleReplayOpen = Number(window.__lanistaBattleReplayOpen || 0) + 1;
    const handleInlineNotice = (event) => {
      const detail = event.detail || {};
      if (!detail.message) return;
      setActiveInlineNotices((prev) => {
        const deduped = detail.id ? prev.filter((notice) => notice.id !== detail.id) : prev;
        return [...deduped, {
          id: detail.id || `notice-${Date.now()}`,
          tone: detail.tone || "info",
          message: detail.message,
        }].slice(-3);
      });
    };

    window.addEventListener("lanista:battle-inline-notice", handleInlineNotice);
    return () => {
      window.__lanistaBattleReplayOpen = Math.max(0, Number(window.__lanistaBattleReplayOpen || 1) - 1);
      window.removeEventListener("lanista:battle-inline-notice", handleInlineNotice);
    };
  }, [open]);

  useEffect(() => {
    if (!battle || !playing) return;
    if (shown >= battle.log.length) { setPlaying(false); return; }
    
    // Calculate delay based on playback speed
    const currentLine = battle.log[shown] || "";
    const delay = (BASE_STEP_MS * linePlaybackFactor(currentLine)) / playbackSpeed;
    
    timer.current = setTimeout(() => setShown((s) => s + 1), delay);
    return () => clearTimeout(timer.current);
  }, [battle, playing, shown, playbackSpeed]);

  useEffect(() => { 
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; 
  }, [shown]);

  useEffect(() => {
    if (!open || !show3D || !battle?.log?.length) {
      setViewerMountReady(false);
      setThreeDLoadPhase(show3D ? "preparing" : "idle");
      return undefined;
    }

    warmBattle3DResources({ immediate: true });
    setViewerMountReady(false);
    setThreeDLoadPhase("warming");
    return scheduleBattle3DMount(() => {
      setThreeDLoadPhase("rendering");
      setViewerMountReady(true);
    });
  }, [battle?.id, battle?.log?.length, open, show3D]);

  useEffect(() => {
    if (!user) return;
    const preferred3D = user.default_battle_view === "3d";
    setShow3D(startIn3D || preferred3D);
  }, [startIn3D, user?.default_battle_view, user]);

  useEffect(() => {
    if (!show3D) {
      setViewerMountReady(false);
      setThreeDLoadPhase("idle");
      return;
    }
    setThreeDLoadPhase((current) => (current === "idle" ? "preparing" : current));
  }, [show3D]);

  const skipToEnd = () => { clearTimeout(timer.current); setPlaying(false); setShown(battle.log.length); };
  const reportFinisher = async () => { try { await api.post(`/battles/${battle.id}/finisher/report`); toast.success("Finisher reported for review"); } catch (e) { toast.error(apiErr(e)); } };
  const done = battle && shown >= battle.log.length;
  const replayFromStart = () => { clearTimeout(timer.current); setShown(1); setPlaying(true); };
  const replayHealth = useMemo(() => battle ? buildBattleReplay3DData(battle) : null, [battle]);

  const setDefaultBattleView = async (view) => {
    if (!user || savingViewPref || user.default_battle_view === view) return;
    setSavingViewPref(true);
    try {
      await api.post("/settings/battle-view", { default_battle_view: view });
      setUser((prev) => (prev ? { ...prev, default_battle_view: view } : prev));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSavingViewPref(false);
    }
  };
  
  // Get current HP state based on shown round
  const getCurrentHP = () => {
    if (!battle?.hp_history || battle.hp_history.length === 0) {
      return getBattleReplay3DFrame(replayHealth, shown)?.hp || { g1_hp: 100, g1_max_hp: 100, g2_hp: 100, g2_max_hp: 100 };
    }
    
    // If battle is done (shown >= log length), return the final HP state
    if (shown >= battle.log.length) {
      return battle.hp_history[battle.hp_history.length - 1];
    }
    
    const currentRound = extractRoundNumber(battle.log[shown] || "");

    // Non-round log lines can appear before the first combat round or after the final round.
    // In those cases, keep the most recent visible combat HP instead of snapping back to full HP.
    if (currentRound === null) {
      for (let i = Math.min(shown - 1, battle.log.length - 1); i >= 0; i -= 1) {
        const visibleRound = extractRoundNumber(battle.log[i]);
        if (visibleRound === null) continue;

        let hpState = battle.hp_history[0];
        for (const state of battle.hp_history) {
          if (state.round <= visibleRound) {
            hpState = state;
          } else {
            break;
          }
        }
        return hpState;
      }

      return battle.hp_history[0];
    }
    
    // Show HP state from BEFORE this round executes
    // If we're showing Round 1, use initial state (hp_history[0])
    // If we're showing Round N, use the state after Round N-1
    let hpState = battle.hp_history[0];
    for (const state of battle.hp_history) {
      if (state.round < currentRound) {  // Changed from <= to <
        hpState = state;
      } else {
        break;
      }
    }
    return hpState;
  };
  
  // Determine which gladiator was hit in the current line (for animation)
  const getHitGladiator = () => {
    if (!battle || shown >= battle.log.length) return null;
    const currentLine = battle.log[shown];
    if (!currentLine || !currentLine.includes("CRITICAL")) return null;
    
    // Parse the CRITICAL line to see who was hit
    // Format is like: "R1: {attacker} ... CRITICAL ... {defender}"
    const g1Name = battle.g1_name || battle.g1;
    const g2Name = battle.g2_name || battle.g2;
    
    // The defender is mentioned after "CRITICAL" in the templates
    // Templates: "{a} finds the perfect opening — a CRITICAL strike that tears into {d}!"
    if (currentLine.includes(g1Name) && currentLine.includes(g2Name)) {
      // Need to determine who is attacker vs defender
      // The format is: attacker's action -> defender gets hit
      // If g1 appears before CRITICAL and g2 after, g2 was hit
      const critIndex = currentLine.indexOf("CRITICAL");
      const g1Index = currentLine.indexOf(g1Name);
      const g2Index = currentLine.indexOf(g2Name);
      
      // Find who appears AFTER the word "CRITICAL" - that's the one being hit
      if (g1Index > critIndex) return "g1";
      if (g2Index > critIndex) return "g2";
    }
    return null;
  };
  
  // Detect attack direction for flash effect (Improvement #2)
  const getAttackDirection = () => {
    if (!battle || shown >= battle.log.length) return null;
    const currentLine = battle.log[shown];
    if (!currentLine) return null;
    
    const g1Name = battle.g1_name || battle.g1;
    const g2Name = battle.g2_name || battle.g2;
    
    // Determine attacker by who appears first in action verbs
    const actionWords = ['strikes', 'attacks', 'lunges', 'slashes', 'thrusts', 'swings', 'CRITICAL'];
    for (const action of actionWords) {
      if (currentLine.includes(action)) {
        const g1Index = currentLine.indexOf(g1Name);
        const g2Index = currentLine.indexOf(g2Name);
        const actionIndex = currentLine.indexOf(action);
        
        // Who appears before the action verb is the attacker
        if (g1Index < actionIndex && g1Index !== -1) return "left";
        if (g2Index < actionIndex && g2Index !== -1) return "right";
      }
    }
    return null;
  };
  
  const currentHP = battle ? getCurrentHP() : null;
  const hitGlad = battle ? getHitGladiator() : null;
  const attackDir = battle ? getAttackDirection() : null;
  const winnerId = battle?.winner_id;
  const showTopLevel3DLoadCurtain = show3D && battle && threeDLoadPhase !== "idle" && threeDLoadPhase !== "ready";
  const topInlineNotices = activeInlineNotices.filter((notice) => notice.tone !== "reward" && notice.position !== "bottom");
  const bottomInlineNotices = activeInlineNotices.filter((notice) => notice.tone === "reward" || notice.position === "bottom");

  const toggle3DView = () => {
    if (show3D) {
      setShow3D(false);
      setViewerMountReady(false);
      setThreeDLoadPhase("idle");
      return;
    }

    flushSync(() => {
      setShow3D(true);
      setViewerMountReady(false);
      setThreeDLoadPhase("preparing");
    });
    warmBattle3DResources({ immediate: true });
  };

  return (
    <Modal open={open} onClose={onClose} title={title} wide>
      {!battle ? (
        <p className="text-zinc-500 text-sm py-10 text-center">Loading the fight…</p>
      ) : (
        <ErrorBoundary fallback={(err) => (
          <div data-testid="battle-replay-error" className="text-center py-8 px-4">
            <div className="font-cinzel text-amber-300 uppercase tracking-wide text-base mb-2">Couldn&apos;t display this fight</div>
            <p className="text-sm text-zinc-400 mb-3">The battle result was saved, but the play-by-play couldn&apos;t be rendered. You can close this and carry on.</p>
            <p className="text-[11px] text-zinc-600 break-words mb-4">{String(err?.message || err)}</p>
            <button data-testid="battle-replay-error-close" onClick={onClose}
              className="font-cinzel uppercase tracking-widest text-xs text-amber-300 border border-amber-700/60 hover:bg-amber-900/30 px-4 py-2 transition-colors">
              Close
            </button>
          </div>
        )}>
        <div data-testid="battle-replay" className={hitGlad ? "animate-screen-shake" : ""}>
          {showTopLevel3DLoadCurtain ? <BattleReplay3DLoadCurtain phase={threeDLoadPhase} /> : null}
          {exhibition && <div className="text-center text-[11px] uppercase tracking-widest text-sky-400 mb-2">Exhibition · No stakes</div>}
          <div className="text-center mb-3">
            <div className="font-cinzel text-xl text-amber-300 uppercase tracking-wide flex items-center justify-center gap-2">
              <Swords size={18} /> <span>{done ? `${battle.winner_name || battle.winner || "The victor"} wins` : "The arena roars…"}</span>
            </div>
            <p className={`text-sm text-zinc-500 ${done ? "" : "hidden"}`} data-testid="battle-summary"><span>{perspectiveSummary(battle, user?.user_id)}</span></p>
            {done && <FightTypeSummaryCard fightType={battle.fight_type} dataTestId="battle-fight-type" />}
            {battle.created_at && (
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-1">
                {new Date(battle.created_at).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true
                })}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="battle-replay-3d-toggle"
                onClick={toggle3DView}
                onMouseEnter={() => warmBattle3DResources()}
                onFocus={() => warmBattle3DResources()}
                className="recruit-pulse flex items-center gap-1.5 text-xs uppercase tracking-widest text-amber-300 hover:text-amber-200 border border-amber-600/70 bg-amber-950/20 hover:border-amber-500 px-3 py-1.5 transition-colors rounded-sm"
              >
                <Cuboid size={14} /> <span>{show3D ? "Hide 3D Battle" : "Watch Battle in 3D"}</span>
              </button>
              {user && (
                <div className="flex items-center gap-1.5 border border-zinc-700 rounded-sm p-0.5" data-testid="battle-replay-default-view-chooser">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 pl-1.5 pr-1 hidden sm:inline">Default:</span>
                  <button
                    data-testid="battle-replay-default-view-text"
                    onClick={() => setDefaultBattleView("text")}
                    disabled={savingViewPref}
                    className={`flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm transition-colors ${(user.default_battle_view || "text") === "text" ? "bg-amber-600 text-zinc-950" : "text-zinc-400 hover:text-amber-300"}`}
                  >
                    <AlignLeft size={12} /> Text/2D
                  </button>
                  <button
                    data-testid="battle-replay-default-view-3d"
                    onClick={() => setDefaultBattleView("3d")}
                    disabled={savingViewPref}
                    className={`flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm transition-colors ${user.default_battle_view === "3d" ? "bg-amber-600 text-zinc-950" : "text-zinc-400 hover:text-amber-300"}`}
                  >
                    <Cuboid size={12} /> 3D
                  </button>
                </div>
              )}
            </div>
            {battle?.log?.length > 0 && (
              <div data-testid="battle-replay-3d-note" className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                {show3D ? "3D replay follows the saved battle result. No outcomes are recalculated." : "Text replay follows the saved battle result. Switch view or change your default anytime."}
              </div>
            )}
          </div>

          <ReplayInlineNotices notices={topInlineNotices} />

          {show3D && (
            <div className="mb-4">
              <Suspense fallback={<BattleReplay3DPreloadShell />}>
                {viewerMountReady ? (
                  <BattleReplay3DViewer
                    battle={battle}
                    shown={shown}
                    playing={playing}
                    setPlaying={setPlaying}
                    setShown={setShown}
                    playbackSpeed={playbackSpeed}
                    setPlaybackSpeed={setPlaybackSpeed}
                    skipToEnd={skipToEnd}
                    onSceneReady={() => setThreeDLoadPhase("ready")}
                  />
                ) : (
                  <BattleReplay3DPreloadShell />
                )}
              </Suspense>
            </div>
          )}

          {!show3D && (
            <BattleReplayClassicView
              battle={battle}
              currentHP={currentHP}
              hitGlad={hitGlad}
              done={done}
              attackDir={attackDir}
              logRef={logRef}
              playing={playing}
              shown={shown}
              winnerId={winnerId}
            />
          )}

          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            {/* Left side - Share button */}
            <div className="flex items-center gap-3">
              {battle?.finisher_event && <button data-testid="report-finisher-button" onClick={reportFinisher} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-rose-300">Report Finisher</button>}
              {shareable && done && battle.id && (
                <button data-testid="replay-share-btn" onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-amber-300 hover:text-amber-200 border border-amber-900/50 bg-amber-950/20 hover:border-amber-600 px-3 py-1.5 transition-colors">
                  <Share2 size={14} /> <span>Share</span>
                </button>
              )}
              <button data-testid="replay-play-pause-btn" onClick={() => setPlaying((value) => !value)}
                className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-1.5 transition-colors rounded-sm">
                {playing ? <Pause size={14} /> : <Play size={14} />} <span>{playing ? "Pause" : "Play"}</span>
              </button>
              <button data-testid="replay-restart-btn" onClick={replayFromStart}
                className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-1.5 transition-colors rounded-sm">
                <Play size={14} /> <span>Replay</span>
              </button>
            </div>
            
            {/* Center - Playback speed controls (only when playing) */}
            {!done && (
              <div className="flex items-center gap-2">
                <Gauge size={14} className="text-zinc-500" />
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map(speed => (
                    <button
                      key={speed}
                      onClick={() => setPlaybackSpeed(speed)}
                      className={`px-2 py-1 text-xs font-semibold transition-colors ${
                        playbackSpeed === speed
                          ? 'bg-amber-600 text-zinc-950 border border-amber-500'
                          : 'bg-zinc-800 text-zinc-400 hover:text-amber-300 border border-zinc-700 hover:border-amber-700/60'
                      }`}
                      title={`${speed}x speed`}>
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Right side - Skip/Rewatch button */}
            {!done ? (
              <button data-testid="replay-skip-btn" onClick={skipToEnd}
                className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-1.5 transition-colors">
                <FastForward size={14} /> <span>Skip to result</span>
              </button>
            ) : (
              <button data-testid="replay-rewatch-btn" onClick={replayFromStart}
                className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-1.5 transition-colors">
                <Play size={14} /> <span>Watch again</span>
              </button>
            )}
          </div>

          {rewards && done && (
            <div className="mt-3" data-testid="replay-rewards">
              {rewards}
            </div>
          )}

          {done && bottomInlineNotices.length > 0 && (
            <div className="mt-3" data-testid="battle-replay-bottom-notices">
              <ReplayInlineNotices notices={bottomInlineNotices} />
            </div>
          )}

          {battle.headline && (
            <div data-testid="replay-headline" className={`text-center font-cinzel text-amber-400 text-base sm:text-lg italic tracking-wide mt-3 px-3 py-2 border-y border-amber-900/40 bg-amber-950/10 ${done ? "" : "hidden"}`}>
              <span>&quot;{battle.headline}&quot;</span>
            </div>
          )}
        </div>
        </ErrorBoundary>
      )}
      {battle?.id && (
        <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} kind="battle" id={battle.id}
          text={battle.winner_name ? `${battle.winner_name} triumphs in the arena! Watch the bout on Lanista.` : "Watch this arena bout on Lanista!"} />
      )}
    </Modal>
  );
}
